<template>
    <div class="ui-page">
        <ui-page-header title="Auditoria" subtitle="Registro das alterações feitas pelos administradores no sistema." />

        <ui-table-card :more-count="moreFiltersCount" clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field v-model="dataList.filter.path" placeholder="Buscar pela rota" prepend-inner-icon="mdi-magnify"
                    dense outlined hide-details class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-select dense outlined hide-details clearable placeholder="Todos os administradores" class="ui-toolbar__field"
                    :items="dataset.admins" item-text="name" item-value="id" :menu-props="{ offsetY: true }"
                    v-model="dataList.filter.admin_id" @change="applyFilters"></v-select>
                <v-select dense outlined hide-details clearable placeholder="Todos os módulos" class="ui-toolbar__field"
                    :items="dataset.modules" :menu-props="{ offsetY: true }" v-model="dataList.filter.module" @change="applyFilters"></v-select>
                <v-select dense outlined hide-details clearable placeholder="Todas as ações" class="ui-toolbar__field"
                    :items="dataset.actions" item-text="name" item-value="id" :menu-props="{ offsetY: true }"
                    v-model="dataList.filter.action" @change="applyFilters"></v-select>
            </template>
            <template #more>
                <v-select dense outlined hide-details clearable placeholder="Todos os status" class="ui-toolbar__field"
                    :items="dataset.statuses" item-text="name" item-value="id" :menu-props="{ offsetY: true }"
                    v-model="dataList.filter.status" @change="applyFilters"></v-select>
                <v-select dense outlined hide-details clearable placeholder="Todos os métodos" class="ui-toolbar__field"
                    :items="dataset.methods" :menu-props="{ offsetY: true }" v-model="dataList.filter.method" @change="applyFilters"></v-select>
                <v-text-field dense outlined hide-details placeholder="ID do recurso" class="ui-toolbar__field"
                    v-model="dataList.filter.resource_id" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field dense outlined hide-details type="date" label="De" class="ui-toolbar__field"
                    v-model="dataList.filter.start_date" @change="applyFilters"></v-text-field>
                <v-text-field dense outlined hide-details type="date" label="Até" class="ui-toolbar__field"
                    v-model="dataList.filter.end_date" @change="applyFilters"></v-text-field>
            </template>

            <v-data-table :headers="dataList.headers" :items="dataList.data" :server-items-length="dataList.count"
                :options.sync="dataList.options" :loading="dataList.loading" disable-sort hide-default-footer
                no-data-text="Nenhum movimento foi encontrado" loading-text="Carregando auditoria..." class="ui-clickable-rows"
                @click:row="openDetails">

                <template v-slot:[`item.admin`]="{ item }">
                    <div class="ui-cell-main">{{ item.admin_name || 'N/A' }}</div>
                    <div class="ui-cell-sub">{{ item.admin_email || 'N/A' }}</div>
                </template>

                <template v-slot:[`item.action`]="{ item }">
                    <ui-badge :text="getActionLabel(item.action)" :tone="getActionTone(item.action)" />
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :text="item.status === 'success' ? 'Sucesso' : 'Erro'" :tone="item.status === 'success' ? 'success' : 'danger'" />
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm") }}
                </template>

                <template v-slot:[`item.path`]="{ item }">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on" class="audit-path">{{ item.path }}</span>
                        </template>
                        <span>{{ item.path }}</span>
                    </v-tooltip>
                </template>

                <template v-slot:[`item.actions`]>
                    <v-icon small class="ui-cell-chevron">mdi-chevron-right</v-icon>
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.itemsPerPage"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>

        <v-dialog v-model="detailsDialog.open" max-width="900px">
            <v-card v-if="detailsDialog.item" class="ui-card">
                <div class="ui-card__header">
                    <h3 class="ui-section-title">Detalhes da auditoria</h3>
                </div>
                <div class="ui-card__body">
                    <v-row>
                        <v-col cols="12" md="6">
                            <div class="ui-label">Administrador</div>
                            <div>{{ detailsDialog.item.admin_name || 'N/A' }}</div>
                            <small class="ui-muted">{{ detailsDialog.item.admin_email || 'N/A' }}</small>
                        </v-col>
                        <v-col cols="12" md="3">
                            <div class="ui-label">Módulo</div>
                            <div>{{ detailsDialog.item.module || 'N/A' }}</div>
                        </v-col>
                        <v-col cols="12" md="3">
                            <div class="ui-label">Data</div>
                            <div>{{ detailsDialog.item.created_at | defaultDate("DD/MM/YYYY HH:mm:ss") }}</div>
                        </v-col>
                        <v-col cols="12">
                            <div class="ui-label">Rota</div>
                            <div>{{ detailsDialog.item.method }} {{ detailsDialog.item.path }}</div>
                        </v-col>
                        <v-col cols="12" md="4">
                            <div class="ui-label">Status HTTP</div>
                            <div>{{ detailsDialog.item.status_code || 'N/A' }}</div>
                        </v-col>
                        <v-col cols="12" md="4">
                            <div class="ui-label">IP</div>
                            <div>{{ detailsDialog.item.user_ip || 'N/A' }}</div>
                        </v-col>
                        <v-col cols="12" md="4">
                            <div class="ui-label">Recurso</div>
                            <div>{{ detailsDialog.item.resource_id || 'N/A' }}</div>
                        </v-col>
                    </v-row>

                    <v-divider class="my-4"></v-divider>

                    <div class="ui-label">Payload</div>
                    <pre class="audit-json">{{ formatJson(detailsDialog.item.request_payload) }}</pre>

                    <div class="ui-label">Query</div>
                    <pre class="audit-json">{{ formatJson(detailsDialog.item.query_payload) }}</pre>

                    <div class="ui-label">Parâmetros</div>
                    <pre class="audit-json">{{ formatJson(detailsDialog.item.params_payload) }}</pre>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined @click="detailsDialog.open = false">Fechar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import _ from "lodash";
import object from "@/common/util/object";

export default {
    name: "AuditLogList",

    data: () => ({
        dataset: {
            admins: [],
            modules: [],
            actions: [],
            methods: [],
            statuses: [],
        },
        dataList: {
            filter: {
                admin_id: null,
                module: null,
                action: null,
                method: null,
                status: null,
                path: "",
                resource_id: "",
                start_date: "",
                end_date: "",
            },
            headers: [
                { text: "Administrador", value: "admin" },
                { text: "Módulo", value: "module" },
                { text: "Ação", value: "action" },
                { text: "Método", value: "method" },
                { text: "Rota", value: "path" },
                { text: "Status", value: "status" },
                { text: "Criado em", value: "created_at" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            ],
            data: [],
            loading: false,
            count: 0,
            options: {
                page: 1,
                itemsPerPage: 10,
            },
            footerOptions: {
                itemsPerPageText: "Itens por página:",
                itemsPerPageOptions: [10, 20, 30, 40, 50],
                pageText: "{0}-{1} de {2}",
            },
        },
        detailsDialog: {
            open: false,
            item: null,
        },
    }),

    computed: {
        // Filtros escondidos em "Mais filtros" que estao preenchidos.
        moreFiltersCount() {
            return [this.dataList.filter.status, this.dataList.filter.method, this.dataList.filter.resource_id, this.dataList.filter.start_date, this.dataList.filter.end_date].filter((value) => value !== null && value !== undefined && value !== "" && value !== false).length;
        },
        hasFilters() {
            return Object.values(this.dataList.filter).some((value) => value);
        },
    },

    async created() {
        await this.loadFilters();
        await this.load();
    },

    methods: {

        applyFilters() {
            this.dataList.options.page = 1;
            this.load();
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.itemsPerPage = size;
        },

        statusChipStyle(status) {

            const bg = status === 'success' ? '#16A34A' : '#DC2626';

            return { 'background-color': bg + ' !important', 'border-color': bg + ' !important', color: '#FFFFFF !important' };

        },
        pageChanged(page) {
            this.dataList.options.page = page;
            this.load();
        },

        pageSizeChanged(size) {
            this.dataList.options.itemsPerPage = size;
            this.load(true);
        },

        async loadFilters() {
            try {
                const result = await axios.get("/api/admin/audit-logs/filters");
                this.dataset = result.data.content;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            }
        },

        async load(resetPage = false) {
            if (resetPage) this.dataList.options.page = 1;

            let search = Object.fromEntries(Object.entries(this.dataList.filter).filter(([_, v]) => v));
            search = !object.isEmpty(search) ? search : null;

            this.dataList.loading = true;

            try {
                const result = await axios.get("/api/admin/audit-logs", {
                    params: {
                        page: this.dataList.options.page,
                        limit: this.dataList.options.itemsPerPage,
                        search,
                    },
                });

                this.dataList.data = result.data.content.data;
                this.dataList.count = result.data.content.paging.total;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
            } finally {
                this.dataList.loading = false;
            }
        },

        clearFilters() {
            this.dataList.filter = {
                admin_id: null,
                module: null,
                action: null,
                method: null,
                status: null,
                path: "",
                resource_id: "",
                start_date: "",
                end_date: "",
            };
            this.applyFilters();
        },

        getActionLabel(action) {
            const labels = {
                create: "Criação",
                update: "Atualização",
                delete: "Exclusão",
            };

            return labels[action] || action || "N/A";
        },

        getActionColor(action) {
            const colors = {
                create: "#2563EB",
                update: "#D97706",
                delete: "#DC2626",
            };

            return colors[action] || "#6B7280";
        },

        getActionTone(action) {
            const tones = {
                create: "info",
                update: "warning",
                delete: "danger",
            };

            return tones[action] || "neutral";
        },

        openDetails(item) {
            this.detailsDialog.item = item;
            this.detailsDialog.open = true;
        },

        formatJson(value) {
            if (!value) return "N/A";

            try {
                return JSON.stringify(JSON.parse(value), null, 2);
            } catch (error) {
                return value;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.audit-path {
    display: inline-block;
    max-width: 260px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: middle;
}

.audit-json {
    background: #F9FAFB;
    border: 1px solid #E5E7EB;
    border-radius: 6px;
    color: #111827;
    font-size: 12px;
    max-height: 220px;
    overflow: auto;
    padding: 12px;
    white-space: pre-wrap;
}
</style>
